import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import apiClient from '../../../api/client';

interface ReactionResult {
  reaction_id: string;
  reaction_smiles: string | null;
  role: string;
  yield_percentage: number | null;
  conditions: Record<string, string> | null;
  source: string;
  dataset_name: string | null;
  user_reaction_id: number | null;
}

interface ReactionsTabProps {
  smiles: string;
  inchikey?: string | null;
  projectId?: number;
}

// SVG cache shared across all instances
const svgCache = new Map<string, string>();

const ReactionThumbnail: React.FC<{ smiles: string }> = ({ smiles }) => {
  const [svg, setSvg] = useState<string | null>(null);
  const cacheKey = `rxn-tab|${smiles}`;
  const cached = svgCache.get(cacheKey);

  useEffect(() => {
    if (cached || !smiles) return;
    let cancelled = false;
    const fetchSvg = async () => {
      try {
        const res = await apiClient.post('/v2/indigo/render', {
          structure: { value: smiles, type: 'RAW' },
          width: '800px',
          height: '200px',
        }, { responseType: 'text' });
        if (!cancelled) {
          svgCache.set(cacheKey, res.data);
          setSvg(res.data);
        }
      } catch {
        // silent — render will just not show
      }
    };
    fetchSvg();
    return () => { cancelled = true; };
  }, [smiles, cacheKey, cached]);

  const displaySvg = cached || svg;

  if (displaySvg) {
    return (
      <div
        className="w-full rounded overflow-hidden [&>svg]:max-w-full [&>svg]:h-auto [&>svg]:mx-auto [&>svg]:block"
        style={{ backgroundColor: 'var(--card-bg)', minHeight: 60, maxHeight: 120 }}
        dangerouslySetInnerHTML={{ __html: displaySvg }}
      />
    );
  }
  return (
    <div className="w-full flex items-center justify-center" style={{ height: 60 }}>
      <div className="w-4 h-4 border-2 border-t-teal-400 rounded-full animate-spin" style={{ borderColor: 'var(--card-border)', borderTopColor: '#14b8a6' }} />
    </div>
  );
};

const roleColors: Record<string, { badge: string; border: string }> = {
  product: { badge: 'text-teal-700 bg-teal-50 border-teal-200', border: '#0d9488' },
  reactant: { badge: 'text-purple-700 bg-purple-50 border-purple-200', border: '#7c3aed' },
  catalyst: { badge: 'text-amber-700 bg-amber-50 border-amber-200', border: '#d97706' },
  reagent: { badge: 'text-orange-700 bg-orange-50 border-orange-200', border: '#ea580c' },
  both: { badge: 'text-blue-700 bg-blue-50 border-blue-200', border: '#2563eb' },
};

const ReactionsTab: React.FC<ReactionsTabProps> = ({ smiles, inchikey, projectId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reactions, setReactions] = useState<ReactionResult[]>([]);
  const [fetched, setFetched] = useState(false);

  // Pop-out: opens the full Reactions Hub in a new tab, pre-filtered to this compound.
  // Matches the 'Open in <Hub>' pattern used by ADMET/Metabolism/SAR tabs (commit 28cdd11).
  const popoutHref = (() => {
    const params = new URLSearchParams({ smiles, popout: 'true' });
    if (projectId != null) params.set('project_id', String(projectId));
    return `/chemistry/reactions?${params.toString()}`;
  })();
  const popoutButton = (
    <button
      onClick={() => window.open(popoutHref, '_blank')}
      title="Open Reactions Hub in a new tab with this compound"
      className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors flex-shrink-0"
      style={{ color: '#0d9488', border: '1px solid rgba(13,148,136,0.3)', backgroundColor: 'transparent' }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(13,148,136,0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <ExternalLink size={11} />
      Open in Reactions Hub
    </button>
  );

  useEffect(() => {
    if (!smiles || fetched) return;

    const fetchReactions = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, any> = { smiles, limit: 50 };
        if (inchikey) params.inchikey = inchikey;

        const response = await apiClient.get('/molecule-reactions', { params });
        setReactions(response.data.results || []);
        setFetched(true);
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Failed to load reactions');
      } finally {
        setLoading(false);
      }
    };

    fetchReactions();
  }, [smiles, inchikey, fetched]);

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Reactions</h4>
          {popoutButton}
        </div>
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-6 h-6 border-2 border-t-teal-500 rounded-full animate-spin" style={{ borderColor: 'var(--card-border)', borderTopColor: '#14b8a6' }} />
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Searching ORD + project reactions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Reactions</h4>
          {popoutButton}
        </div>
        <div className="p-4 rounded-lg" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
          <p className="text-sm" style={{ color: '#dc2626' }}>{error}</p>
          <button
            onClick={() => { setFetched(false); setError(null); }}
            className="mt-2 text-xs underline hover:no-underline"
            style={{ color: '#991b1b' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (fetched && reactions.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Reactions</h4>
          {popoutButton}
        </div>
        <div className="text-center py-8">
          <svg className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
          </svg>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No known synthesis routes found</p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Not found in the Open Reaction Database or project reactions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-[500px] overflow-y-auto">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
          Reactions ({reactions.length})
        </h4>
        {popoutButton}
      </div>
      {reactions.map((rxn, idx) => {
        const roleStyle = roleColors[rxn.role] || roleColors.reactant;
        const conditionParts: string[] = [];
        if (rxn.conditions) {
          if (rxn.conditions.solvent) conditionParts.push(rxn.conditions.solvent);
          if (rxn.conditions.temperature) conditionParts.push(rxn.conditions.temperature);
          if (rxn.conditions.time) conditionParts.push(rxn.conditions.time);
        }

        return (
          <div
            key={`${rxn.reaction_id}-${idx}`}
            className="rounded overflow-hidden"
            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderLeft: `3px solid ${roleStyle.border}` }}
          >
            {/* SVG thumbnail */}
            {rxn.reaction_smiles && (
              <div style={{ backgroundColor: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)' }}>
                <ReactionThumbnail smiles={rxn.reaction_smiles} />
              </div>
            )}

            {/* Metadata row */}
            <div className="p-2.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                {/* Role badge */}
                <span className={`text-xs px-1.5 py-0.5 rounded border font-medium ${roleStyle.badge}`}>
                  {rxn.role}
                </span>

                {/* Source badge */}
                <span className="text-xs px-1.5 py-0.5 rounded border"
                  style={rxn.source === 'ORD'
                    ? { color: 'var(--text-secondary)', backgroundColor: 'var(--hover-bg)', borderColor: 'var(--card-border)' }
                    : { color: '#0d9488', backgroundColor: '#f0fdfa', borderColor: '#99f6e4' }
                  }>
                  {rxn.source}
                </span>

                {/* Yield */}
                {rxn.yield_percentage != null && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                    {rxn.yield_percentage}%
                  </span>
                )}

                {/* Conditions */}
                {conditionParts.length > 0 && (
                  <span className="text-xs ml-auto truncate max-w-[140px]" style={{ color: 'var(--text-secondary)' }} title={conditionParts.join(', ')}>
                    {conditionParts.join(', ')}
                  </span>
                )}
              </div>

              {/* Dataset / ID */}
              <div className="mt-1 flex items-center gap-2">
                <span
                  className="truncate"
                  style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono, monospace)', fontSize: 10 }}
                  title={rxn.reaction_id}
                >
                  {rxn.reaction_id.length > 30 ? rxn.reaction_id.substring(0, 30) + '...' : rxn.reaction_id}
                </span>
                {rxn.dataset_name && (
                  <span className="text-xs truncate" style={{ color: 'var(--text-muted)' }} title={rxn.dataset_name}>
                    {rxn.dataset_name.length > 25 ? rxn.dataset_name.substring(0, 25) + '...' : rxn.dataset_name}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ReactionsTab;
